import React, { useEffect, useState } from "react";
import { Chip, Tooltip } from "@nextui-org/react";
import {
  FaShieldHalved, FaUserGear, FaHeadset, FaBuilding,
  FaCircleCheck, FaCircleUser, FaCode, FaFlaskVial, FaHandshake, FaCrown,
} from "react-icons/fa6";
import { API_URL } from "../../config/api";
import { BADGE_ICONS } from "./badgeIcons";

// Centralized definition of all possible badges: "system" roles +
// extra badges assignable by admins (users.badges field, JSON array).
const ROLE_BADGES = {
  admin: { label: "Admin", color: "danger", tooltip: "Neko-CLI Administrator", Icon: FaShieldHalved },
  moderator: { label: "Moderator", color: "warning", tooltip: "Community moderator", Icon: FaUserGear },
  support: { label: "Support", color: "primary", tooltip: "Official support staff", Icon: FaHeadset },
  enterprise: { label: "Enterprise", color: "secondary", tooltip: "Enterprise account", Icon: FaBuilding },
};

const EXTRA_BADGES = {
  signed: { label: "Signed", color: "danger", tooltip: "Account verified and signed by the Neko-CLI staff", Icon: FaCircleCheck },
  verified: { label: "Verified", color: "success", tooltip: "Verified identity", Icon: FaCircleUser },
  contributor: { label: "Contributor", color: "secondary", tooltip: "Contributed to the Neko-CLI project", Icon: FaCode },
  beta_tester: { label: "Beta Tester", color: "primary", tooltip: "Participates in beta version testing", Icon: FaFlaskVial },
  partner: { label: "Partner", color: "warning", tooltip: "Official Neko-CLI partner", Icon: FaHandshake },
  og: { label: "OG", color: "default", tooltip: "Long-standing community member", Icon: FaCrown },
};

// Custom (admin-created) badges are fetched once from /api/badges and kept
// in a module-level cache — every place that renders UserBadges (blog,
// forum, profile, admin) shares the same fetch instead of each doing its own.
let customBadgesCache = null;
let customBadgesPromise = null;
const subscribers = new Set();

function loadCustomBadges() {
  if (customBadgesCache) return Promise.resolve(customBadgesCache);
  if (!customBadgesPromise) {
    customBadgesPromise = fetch(`${API_URL}/api/badges`)
      .then((r) => r.json())
      .then((d) => {
        customBadgesCache = d.badges || [];
        subscribers.forEach((fn) => fn(customBadgesCache));
        return customBadgesCache;
      })
      .catch(() => {
        customBadgesCache = [];
        return customBadgesCache;
      });
  }
  return customBadgesPromise;
}

function useCustomBadges() {
  const [list, setList] = useState(customBadgesCache || []);
  useEffect(() => {
    if (customBadgesCache) return;
    subscribers.add(setList);
    loadCustomBadges();
    return () => subscribers.delete(setList);
  }, []);
  return list;
}

export default function UserBadges({ role, badges = [], size = "sm", className = "" }) {
  const customBadges = useCustomBadges();
  const chips = [];

  if (role && ROLE_BADGES[role]) {
    chips.push({ key: `role-${role}`, ...ROLE_BADGES[role] });
  }
  (badges || []).forEach((b) => {
    if (EXTRA_BADGES[b]) {
      chips.push({ key: `badge-${b}`, ...EXTRA_BADGES[b] });
      return;
    }
    const custom = customBadges.find((c) => c.key === b);
    if (custom) {
      const { Icon } = BADGE_ICONS[custom.icon] || BADGE_ICONS.star;
      chips.push({
        key: `badge-${b}`,
        label: custom.label,
        tooltip: custom.label,
        Icon,
        customColor: custom.color,
      });
    }
  });

  if (chips.length === 0) return null;

  const iconSize = size === "lg" ? 13 : size === "md" ? 12 : 11;

  return (
    <div className={`flex flex-wrap gap-1.5 ${className}`}>
      {chips.map((c) => (
        <Tooltip key={c.key} content={c.tooltip} closeDelay={0}>
          <Chip
            size={size}
            color={c.customColor ? "default" : c.color}
            variant="bordered"
            className="font-medium"
            style={c.customColor ? { borderColor: c.customColor, color: c.customColor } : undefined}
            startContent={<c.Icon size={iconSize} className="ml-1" />}
          >
            {c.label}
          </Chip>
        </Tooltip>
      ))}
    </div>
  );
}
